import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useInView } from "@/hooks/useInView";
import { useActivePromotion } from "@/hooks/useActivePromotion";
import axios from "axios";
import { PHOTOS } from "@/assets/photos";
import { Camera, Star, MapPin, Clock, Printer, Sparkles, Monitor, Truck, ChevronRight, Phone, Mail, Instagram, Facebook, MessageCircle } from "lucide-react";
import logo from "@/assets/logo.png";
import { getBackendUrl } from "@/utils/apiBase";

const BACKEND_URL = getBackendUrl();

const heroSlides = [
  { img: PHOTOS.hero, label: "Mariages & Fêtes", title: "Capturez vos plus beaux moments", sub: "Location de photobooth haut de gamme en Île-de-France" },
  { img: PHOTOS.roses, label: "Ambiance Unique", title: "Une expérience inoubliable", sub: "Décor élégant, lumières et accessoires inclus" },
  { img: PHOTOS.party, label: "Tous Événements", title: "Du rire, de la joie, des souvenirs", sub: "Anniversaires, baptêmes, soirées d'entreprise…" },
];

const reviews = [
  { name: "Sarah M.", event: "Mariage Juin 2024", text: "Un service impeccable ! Le photobooth a été la star de notre soirée. Les invités se sont éclatés et les photos sont de super qualité." },
  { name: "Karim B.", event: "Anniversaire Sept. 2024", text: "Ponctuel, professionnel et sympa. La borne est moderne et les accessoires top. Je recommande les yeux fermés !" },
  { name: "Amina D.", event: "Baptême Oct. 2024", text: "La prise en main à distance était impeccable, tout s'est déroulé parfaitement. Le rendu des photos est magnifique." },
];

// ——— Animated Section Wrapper ———
const AnimatedSection = ({ children, className = "", delay = 0 }) => {
  const [ref, isInView] = useInView();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? "translateY(0)" : "translateY(30px)",
        transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
      }}
    >
      {children}
    </div>
  );
};

// ——— Navbar ———
const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navBg = scrolled ? "bg-white shadow-md" : "bg-transparent";
  const textColor = scrolled ? "text-[#1a1a1a]" : "text-white";

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`} data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img
            src={logo}
            alt="SelfieBooth Event"
            style={{ height: "48px", width: "auto", filter: scrolled ? "none" : "brightness(0) invert(1)", transition: "filter 0.35s ease" }}
            data-testid="nav-logo"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8">
          {[
            { label: "Accueil", href: "/" },
            { label: "Galerie", href: "/galerie" },
            { label: "Calendrier", href: "/calendar" },
            { label: "Réserver", href: "/booking" },
          ].map((l) => (
            <Link key={l.href} to={l.href} className={`font-['DM_Sans'] text-sm font-medium ${textColor} hover:text-[#b8977e] transition-colors`}>
              {l.label}
            </Link>
          ))}
          <Link
            to="/landing"
            className="font-['DM_Sans'] text-sm font-semibold bg-[#b8977e] text-white px-6 py-2.5 rounded-full hover:bg-[#a68770] transition-colors"
            data-testid="nav-cta"
          >
            Devis gratuit
          </Link>
        </div>

        {/* Mobile burger */}
        <button onClick={() => setMenuOpen(!menuOpen)} className={`md:hidden ${textColor}`}>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {menuOpen ? <path strokeLinecap="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /> : <path strokeLinecap="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
          </svg>
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-white shadow-lg border-t">
          <div className="flex flex-col px-6 py-4 gap-4">
            {[
              { label: "Accueil", href: "/" },
              { label: "Galerie", href: "/galerie" },
              { label: "Calendrier", href: "/calendar" },
              { label: "Réserver", href: "/booking" },
            ].map((l) => (
              <Link key={l.href} to={l.href} onClick={() => setMenuOpen(false)} className="font-['DM_Sans'] text-[#1a1a1a] text-sm font-medium">
                {l.label}
              </Link>
            ))}
            <Link to="/landing" onClick={() => setMenuOpen(false)} className="font-['DM_Sans'] text-sm font-semibold bg-[#b8977e] text-white px-6 py-2.5 rounded-full text-center">
              Devis gratuit
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

// ——— Hero Carousel ———
const HeroCarousel = () => {
  const [current, setCurrent] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => setCurrent((p) => (p + 1) % heroSlides.length), 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative h-screen overflow-hidden" data-testid="hero-carousel">
      {heroSlides.map((slide, i) => (
        <div
          key={i}
          className="absolute inset-0 transition-opacity duration-[800ms]"
          style={{ opacity: current === i ? 1 : 0 }}
        >
          <img src={slide.img} alt={slide.label} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
        </div>
      ))}

      <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
        <span className="font-['DM_Sans'] text-[#b8977e] uppercase tracking-[0.25em] text-sm font-medium mb-4">
          {heroSlides[current].label}
        </span>
        <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight max-w-4xl mb-6">
          {heroSlides[current].title}
        </h1>
        <p className="font-['Cormorant_Garamond'] italic text-white/80 text-lg sm:text-xl lg:text-2xl max-w-2xl mb-10">
          {heroSlides[current].sub}
        </p>

        <div className="flex flex-col sm:flex-row gap-4">
          <button
            onClick={() => navigate("/booking")}
            className="font-['DM_Sans'] font-semibold bg-[#b8977e] text-white px-8 py-3.5 rounded-full hover:bg-[#a68770] transition-colors text-sm"
            data-testid="hero-cta-book"
          >
            Réserver maintenant
          </button>
          <button
            onClick={() => navigate("/landing")}
            className="font-['DM_Sans'] font-semibold border-2 border-white text-white px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors text-sm"
            data-testid="hero-cta-quote"
          >
            Obtenir un devis
          </button>
        </div>
      </div>

      {/* Dots */}
      <div style={{ position: "absolute", bottom: "5.5rem", left: "50%", transform: "translateX(-50%)", display: "flex", gap: "0.6rem", zIndex: 3 }}>
        {heroSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`rounded-full transition-all duration-300 ${current === i ? "w-8 h-2.5 bg-[#b8977e]" : "w-2.5 h-2.5 bg-white/50 hover:bg-white/80"}`}
            aria-label={`Slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Scroll indicator */}
      <style>{`
        @keyframes scrollPulse {
          0%, 100% { opacity: 0.3; transform: scaleY(0.8); }
          50% { opacity: 1; transform: scaleY(1); }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(6px); }
        }
      `}</style>
      <div style={{ position: "absolute", bottom: "1.5rem", left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: "0.5rem", zIndex: 3 }}>
        <div style={{ width: 1, height: 35, background: "linear-gradient(to bottom, transparent, rgba(255,255,255,0.7))", animation: "scrollPulse 2s ease-in-out infinite" }} />
        <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "0.65rem", letterSpacing: "0.15em", textTransform: "uppercase", fontFamily: "'DM Sans', sans-serif", animation: "bounce 1.5s ease-in-out infinite" }}>DÉCOUVRIR</span>
      </div>
    </section>
  );
};

// ——— Stats ———
const StatsSection = () => {
  const stats = [
    { value: "150+", label: "Événements réalisés" },
    { value: "98%", label: "Clients satisfaits" },
    { value: "3", label: "Forfaits sur mesure" },
    { value: "Île‑de‑France", label: "Zone d'intervention", small: true },
  ];

  return (
    <section className="bg-[#f5ede8] py-16 md:py-20">
      <div className="max-w-5xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
        {stats.map((s, i) => (
          <AnimatedSection key={i} delay={i * 0.1} className="text-center">
            <p className={`font-['Playfair_Display'] font-bold text-[#1a1a1a] ${s.small ? "" : "text-3xl sm:text-4xl"}`} style={s.small ? { fontSize: "1.2rem" } : undefined}>{s.value}</p>
            <p className="font-['DM_Sans'] text-sm text-[#777] mt-2">{s.label}</p>
          </AnimatedSection>
        ))}
      </div>
    </section>
  );
};

// ——— About ———
const AboutSection = () => {
  const features = ["Impression instantanée", "Accessoires & props inclus", "Galerie digitale", "Livraison & installation", "Photobooth autonome", "Prise en main à distance"];
  const navigate = useNavigate();

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-[1100px] mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 items-center">
        <AnimatedSection>
          <div className="relative">
            <img
              src={PHOTOS.setup}
              alt="Setup photobooth SelfieBooth Event"
              className="w-full h-[380px] sm:h-[480px] object-cover rounded-[2rem] shadow-2xl"
            />
            <div className="absolute bottom-4 right-4 bg-white rounded-2xl px-4 py-3 shadow-lg flex items-center gap-2">
              <Camera className="w-5 h-5 text-[#b8977e]" />
              <span className="font-['DM_Sans'] text-sm font-medium text-[#1a1a1a]">Setup professionnel</span>
            </div>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.15}>
          <span className="font-['DM_Sans'] text-[#b8977e] uppercase tracking-[0.2em] text-xs font-semibold">Notre histoire</span>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#1a1a1a] mt-4 mb-6">
            Une passion pour vos souvenirs
          </h2>
          <p className="font-['DM_Sans'] text-[#555] leading-relaxed mb-4">
            Chez SelfieBooth Event, nous croyons que chaque événement mérite des souvenirs exceptionnels. Notre photobooth haut de gamme apporte une touche de magie à vos mariages, anniversaires et événements d'entreprise en Île-de-France.
          </p>
          <p className="font-['DM_Sans'] text-[#555] leading-relaxed mb-8">
            Avec un matériel professionnel, des impressions instantanées et un service clé en main, nous transformons chaque instant en souvenir tangible que vos invités emporteront chez eux.
          </p>
          <ul className="space-y-3 mb-8">
            {features.map((f, i) => (
              <li key={i} className="flex items-center gap-3 font-['DM_Sans'] text-[#1a1a1a] text-sm">
                <span className="text-[#b8977e]">✦</span> {f}
              </li>
            ))}
          </ul>
          <button
            onClick={() => navigate("/booking")}
            className="font-['DM_Sans'] font-semibold bg-[#b8977e] text-white px-7 py-3 rounded-full hover:bg-[#a68770] transition-colors text-sm"
          >
            Réserver votre date
          </button>
        </AnimatedSection>
      </div>
    </section>
  );
};

// ——— Gallery ———
const GallerySection = () => {
  const navigate = useNavigate();
  const photos = [PHOTOS.hero, PHOTOS.roses, PHOTOS.guests, PHOTOS.party];

  return (
    <section className="py-20 md:py-24 bg-[#fafafa]">
      <div className="max-w-[1000px] mx-auto px-6 text-center">
        <AnimatedSection>
          <span className="font-['DM_Sans'] text-[#b8977e] uppercase tracking-[0.2em] text-xs font-semibold">Nos réalisations</span>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#1a1a1a] mt-4 mb-12">
            Ils nous ont fait confiance
          </h2>
        </AnimatedSection>

        <div className="grid grid-cols-2 gap-3 sm:gap-5 mb-10">
          {photos.map((p, i) => (
            <AnimatedSection key={i} delay={i * 0.1}>
              <div className="group relative overflow-hidden rounded-[1.2rem]">
                <img
                  src={p}
                  alt={`Réalisation ${i + 1}`}
                  className="w-full h-52 sm:h-72 object-cover transition-transform duration-500 group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#b8977e]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-5">
                  <span className="font-['DM_Sans'] text-white text-sm font-medium">Voir plus</span>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        <AnimatedSection delay={0.4}>
          <button
            onClick={() => navigate("/galerie")}
            className="font-['DM_Sans'] font-semibold border-2 border-[#b8977e] text-[#b8977e] px-7 py-3 rounded-full hover:bg-[#b8977e] hover:text-white transition-colors text-sm inline-flex items-center gap-2"
          >
            Voir toute la galerie <ChevronRight className="w-4 h-4" />
          </button>
        </AnimatedSection>
      </div>
    </section>
  );
};

// ——— Packages ———
const PackagesSection = () => {
  const navigate = useNavigate();
  const { promo, getDiscountedPrice } = useActivePromotion();

  const pkgList = [
    { id: "3h", name: "Forfait 3 heures", hours: "3 heures", price: 270, featured: false, features: ["Photobooth autonome", "1 imprimante incluse", "Impressions illimitées qualité photo", "Accessoires & props inclus", "Galerie digitale offerte", "Livraison & installation IDF incluses", "Prise en main à distance"] },
    { id: "5h", name: "Forfait 5 heures", hours: "5 heures", price: 340, featured: true, features: ["Photobooth autonome", "1 imprimante incluse", "Impressions illimitées qualité photo", "Accessoires & props inclus", "Galerie digitale offerte", "Livraison & installation IDF incluses", "Prise en main à distance"] },
    { id: "7h", name: "Forfait 7 heures", hours: "7 heures", price: 400, featured: false, features: ["Photobooth autonome", "1 imprimante incluse", "Impressions illimitées qualité photo", "Accessoires & props inclus", "Galerie digitale offerte", "Livraison & installation IDF incluses", "Prise en main à distance"] },
  ];

  const getPrice = (base, forfaitId) => {
    const r = getDiscountedPrice(base, forfaitId);
    return { original: r.originalPrice, final: r.discountedPrice, hasDiscount: r.hasDiscount, promoLabel: r.promoLabel };
  };

  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-[1000px] mx-auto px-6 text-center">
        <AnimatedSection>
          <span className="font-['DM_Sans'] text-[#b8977e] uppercase tracking-[0.2em] text-xs font-semibold">Nos forfaits</span>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-[#1a1a1a] mt-4 mb-3">
            Choisissez votre formule
          </h2>
          <p className="font-['DM_Sans'] text-[#777] text-sm mb-14 max-w-lg mx-auto">
            Sans engagement, livraison et installation incluses en Île-de-France
          </p>
        </AnimatedSection>

        <div className="grid md:grid-cols-3 gap-6 items-center">
          {pkgList.map((pkg, i) => {
            const p = getPrice(pkg.price, pkg.id);
            return (
            <AnimatedSection key={i} delay={i * 0.12}>
              <div
                className={`relative rounded-3xl p-8 text-left transition-shadow ${
                  pkg.featured
                    ? "bg-[#1a1a1a] text-white scale-[1.04] shadow-2xl"
                    : "bg-white border-2 border-[#f5ede8] shadow-md hover:shadow-lg"
                }`}
              >
                {pkg.featured && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#b8977e] text-white font-['DM_Sans'] text-xs font-semibold px-5 py-1.5 rounded-full">
                    Populaire
                  </div>
                )}

                {p.hasDiscount && (
                  <div className={`absolute -top-3.5 right-4 font-['DM_Sans'] text-xs font-bold px-3 py-1.5 rounded-full ${pkg.featured ? "bg-[#b8977e] text-white" : "bg-[#b8977e] text-white"}`}>
                    {p.promoLabel}
                  </div>
                )}

                <h3 className={`font-['Playfair_Display'] text-xl font-bold ${pkg.featured ? "text-white" : "text-[#1a1a1a]"}`}>
                  {pkg.name}
                </h3>
                <p className={`font-['DM_Sans'] text-sm mt-1 ${pkg.featured ? "text-white/60" : "text-[#999]"}`}>
                  {pkg.hours}
                </p>

                <div className="flex items-baseline gap-2 mt-5 mb-6">
                  {p.hasDiscount && (
                    <span className={`font-['DM_Sans'] text-base line-through ${pkg.featured ? "text-white/40" : "text-[#bbb]"}`}>
                      {p.original}€
                    </span>
                  )}
                  <span className={`font-['Playfair_Display'] text-3xl font-bold ${pkg.featured ? "text-[#b8977e]" : "text-[#1a1a1a]"}`}>
                    {p.final}€
                  </span>
                </div>

                <ul className="space-y-3 mb-8">
                  {pkg.features.map((f, fi) => (
                    <li key={fi} className={`flex items-center gap-2.5 font-['DM_Sans'] text-sm ${pkg.featured ? "text-white/80" : "text-[#555]"}`}>
                      <svg className="w-4 h-4 flex-shrink-0 text-[#b8977e]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => navigate(`/reserver?forfait=${pkg.id}`)}
                  className={`w-full font-['DM_Sans'] font-semibold py-3 rounded-full text-sm transition-colors ${
                    pkg.featured
                      ? "bg-white text-[#b8977e] hover:bg-gray-100"
                      : "bg-[#b8977e] text-white hover:bg-[#a68770]"
                  }`}
                >
                  Choisir ce forfait
                </button>
              </div>
            </AnimatedSection>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ——— Reviews ———
const ReviewsSection = () => {
  const [apiReviews, setApiReviews] = useState([]);
  useEffect(() => {
    // Public endpoint — only active reviews (no admin token needed)
    axios.get(`${BACKEND_URL}/api/reviews/active`).then(res => {
      const data = res.data;
      const active = Array.isArray(data) ? data : [];
      if (active.length > 0) setApiReviews(active);
    }).catch(() => {});
  }, []);

  const displayReviews = apiReviews.length > 0
    ? apiReviews.slice(0, 6).map(r => ({ name: r.client_name, event: "", text: r.review_text, rating: r.rating }))
    : reviews.map(r => ({ ...r, rating: 5 }));

  return (
  <section className="py-20 md:py-28" style={{ background: "linear-gradient(180deg, #1a1a1a 0%, #2a2020 100%)" }}>
    <div className="max-w-5xl mx-auto px-6 text-center">
      <AnimatedSection>
        <span className="font-['DM_Sans'] text-[#b8977e] uppercase tracking-[0.2em] text-xs font-semibold">Témoignages</span>
        <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-bold text-white mt-4 mb-14">
          Ce que disent nos clients
        </h2>
      </AnimatedSection>

      <div className="grid md:grid-cols-3 gap-6">
        {displayReviews.map((r, i) => (
          <AnimatedSection key={i} delay={i * 0.12}>
            <div className="bg-white rounded-[1.5rem] p-7 text-left shadow-lg">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, si) => (
                  <Star key={si} className={`w-4 h-4 ${si < r.rating ? "fill-[#f4b942] text-[#f4b942]" : "text-gray-300"}`} />
                ))}
              </div>
              <p className="font-['Cormorant_Garamond'] italic text-[#333] text-base leading-relaxed mb-5">
                "{r.text}"
              </p>
              <div>
                <p className="font-['DM_Sans'] font-semibold text-[#1a1a1a] text-sm">{r.name}</p>
                {r.event && <p className="font-['DM_Sans'] text-[#999] text-xs">{r.event}</p>}
              </div>
            </div>
          </AnimatedSection>
        ))}
      </div>
    </div>
  </section>
  );
};

// ——— CTA ———
const CtaSection = () => {
  const navigate = useNavigate();
  return (
    <section className="py-20 md:py-28 bg-[#f5ede8] text-center">
      <div className="max-w-3xl mx-auto px-6">
        <AnimatedSection>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1a1a1a] mb-5">
            Votre événement mérite le meilleur
          </h2>
          <p className="font-['DM_Sans'] text-[#777] mb-10">
            Disponibilités limitées — Réservez dès maintenant pour votre date
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/booking")}
              className="font-['DM_Sans'] font-semibold bg-[#b8977e] text-white px-8 py-3.5 rounded-full hover:bg-[#a68770] transition-colors text-sm"
            >
              Réserver maintenant
            </button>
            <a
              href="https://wa.me/33679393429"
              target="_blank"
              rel="noopener noreferrer"
              className="font-['DM_Sans'] font-semibold border-2 border-[#1a1a1a] text-[#1a1a1a] px-8 py-3.5 rounded-full hover:bg-[#1a1a1a] hover:text-white transition-colors text-sm inline-flex items-center justify-center gap-2"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp
            </a>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

// ——— Footer removed: unified global <Footer /> is mounted at App level (Aug 2026) ———

// ——— Main Page ———
export default function HomePage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <HeroCarousel />
      <StatsSection />
      <AboutSection />
      <GallerySection />
      <PackagesSection />
      <ReviewsSection />
      <CtaSection />
    </div>
  );
}
